import { useEffect, useRef } from "react";
import {
  createFilePondExtensionSet,
  type FilePondElement,
  type FilePondEntry,
  type Locale as FilepondLocale,
  type PublicRequestOptions,
} from "filepond";
import {
  FormPostStore,
  type FormPostStoreOptions,
} from "filepond/extensions/form-post-store";
import { locale as localeEn } from "filepond/locales/en-gb";
import { locale as localeEs } from "filepond/locales/es-es";
import { getApiBaseUrl } from "@apps/dashboard/lib/env";
import {
  DASHBOARD_STORAGE_ALLOWED_EXTENSIONS_IMAGES,
  DASHBOARD_STORAGE_ALLOWED_EXTENSIONS_VAULT,
  DASHBOARD_STORAGE_ALLOWED_MIME_IMAGES,
  DASHBOARD_STORAGE_ALLOWED_MIME_VAULT,
  DASHBOARD_STORAGE_MAX_SIZE_LABEL,
} from "@apps/dashboard/lib/storage-upload-config";
import type { Locale } from "@project/i18n";
import { localeToAcceptLanguageHeader } from "@project/i18n";

function acceptLanguageHeader(locale: Locale): string {
  return localeToAcceptLanguageHeader(locale);
}

function filepondLocaleBundle(appLocale: Locale): FilepondLocale {
  return (appLocale === "en" ? localeEn : localeEs) as unknown as FilepondLocale;
}

function parseStoredKeys(entries: FilePondEntry[]): string[] {
  const keys: string[] = [];
  for (const e of entries) {
    const raw = e.state?.value;
    if (typeof raw !== "string" || !raw.trim()) continue;
    try {
      const j = JSON.parse(raw) as { key?: string };
      if (typeof j.key === "string" && j.key.length > 0) keys.push(j.key);
    } catch {
      /* respuesta no JSON */
    }
  }
  return keys;
}

export type FilePondStorageUploadMode = "vault" | "images";

export interface FilePondStorageUploadProps {
  locale: Locale;
  mode: FilePondStorageUploadMode;
  maxFiles?: number;
  onStoredKeys: (keys: string[]) => void;
  disabled?: boolean;
  className?: string;
  inputId?: string;
}

export function FilePondStorageUpload({
  locale,
  mode,
  maxFiles = 1,
  onStoredKeys,
  disabled,
  className,
  inputId = "storage-file-upload",
}: FilePondStorageUploadProps): JSX.Element {
  const pondRef = useRef<FilePondElement | null>(null);
  const pondWithExtensionsRef = useRef<FilePondElement | null>(null);
  const onStoredKeysRef = useRef(onStoredKeys);
  onStoredKeysRef.current = onStoredKeys;

  /** Sin `image/*` mezclado con `.pdf`: FilePond solo tomaba `.pdf` para FileExtensionValidator. */
  const acceptAttr =
    mode === "vault"
      ? "image/jpeg,image/png,image/webp,image/gif,application/pdf"
      : "image/jpeg,image/png,image/webp,image/gif";

  useEffect(() => {
    const el = pondRef.current;
    if (!el) return;

    /** Sin esto, `FormPostStore` no existe en el manager: el setter del CE no llega al upload real. */
    if (pondWithExtensionsRef.current !== el) {
      el.extensions = createFilePondExtensionSet([FormPostStore]);
      pondWithExtensionsRef.current = el;
    }

    const mimeList =
      mode === "vault"
        ? [...DASHBOARD_STORAGE_ALLOWED_MIME_VAULT]
        : [...DASHBOARD_STORAGE_ALLOWED_MIME_IMAGES];
    const extensionList =
      mode === "vault"
        ? [...DASHBOARD_STORAGE_ALLOWED_EXTENSIONS_VAULT]
        : [...DASHBOARD_STORAGE_ALLOWED_EXTENSIONS_IMAGES];

    const pond = el as FilePondElement & { FormPostStore: FormPostStoreOptions };

    el.disabled = Boolean(disabled);
    el.locale = filepondLocaleBundle(locale);
    el.maxFiles = maxFiles;
    el.multiple = maxFiles > 1;
    el.maxSize = DASHBOARD_STORAGE_MAX_SIZE_LABEL;
    el.noAttribution = true;
    el.FileMimeTypeValidator = { accept: mimeList };
    el.FileExtensionValidator = { accept: extensionList };
    pond.FormPostStore = {
      url: `${getApiBaseUrl().replace(/\/+$/, "")}/storage/upload`,
      name: "file",
      fetchHead: false,
      shouldStore: async () => true,
      willRequestWithOptions: (_url: string, options: PublicRequestOptions): PublicRequestOptions => ({
        ...options,
        withCredentials: true,
        headers: {
          ...options.headers,
          Accept: "application/json",
          "Accept-Language": acceptLanguageHeader(locale),
        },
      }),
    };

    const handleChange = (): void => {
      const keys = parseStoredKeys(el.entries);
      if (keys.length > 0) {
        onStoredKeysRef.current(keys);
      }
    };

    el.addEventListener("change", handleChange);
    return () => {
      el.removeEventListener("change", handleChange);
    };
  }, [locale, maxFiles, mode, disabled]);

  return (
    <div className={className}>
      <file-pond ref={pondRef}>
        <input id={inputId} name="file" type="file" accept={acceptAttr} />
      </file-pond>
    </div>
  );
}
